import { ApolloQueryResult, OperationVariables } from "@apollo/client";
import {
  ChildDataProps,
  withApollo,
  WithApolloClient,
} from "@apollo/client/react/hoc";
import { uniqBy } from "lodash";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { ssm } from "../../state/session/ssm";
import {
  CreateFileMutationFn,
  CreateSiteMutationFn,
  SiteListProps,
  SiteListQuery,
  SiteListQueryVariables,
  UpdateSiteByIdMutationFn,
  withCreateFile,
  withCreateSite,
  withSiteList,
  withUpdateSiteById,
} from "../../types.g";
import { compose } from "../../utils/compose";

export interface ApolloProps
  extends RouteComponentProps<any>,
    WithApolloClient<{}> {
  loadMore: () => Promise<ApolloQueryResult<SiteListQuery>>;
  createSite: CreateSiteMutationFn;
  createFile: CreateFileMutationFn;
  updateSiteById: UpdateSiteByIdMutationFn;
}

export const withData = compose(
  withRouter,
  withApollo,
  withCreateSite({
    name: "createSite",
  }),
  withSiteList({
    options: () => {
      const queryVar: SiteListQueryVariables = {
        spaceId: ssm.current.settings.spaceId,
        endCursor: null,
        orderBy: null,
        first: 20,
        filter: {},
      };

      return {
        fetchPolicy: "cache-and-network",
        variables: queryVar,
      };
    },
    props: ({
      data,
      ownProps,
    }): ChildDataProps<
      OperationVariables,
      SiteListQuery,
      SiteListQueryVariables
    > => {
      if (data?.spaceById?.availableSitesBySpaceId?.pageInfo) {
        const variables = {
          endCursor: data.spaceById.availableSitesBySpaceId.pageInfo.endCursor,
        };
        const tmpData = {
          ...(ownProps as SiteListProps),
          loadMore: () =>
            data.fetchMore({
              updateQuery: (previousResult, { fetchMoreResult }) => {
                const prevLinkData =
                  previousResult.spaceById!.availableSitesBySpaceId;
                const currentLinkData =
                  fetchMoreResult!.spaceById!.availableSitesBySpaceId;
                const siteNodes = uniqBy(
                  [...prevLinkData.nodes, ...currentLinkData.nodes],
                  "id",
                );
                return {
                  ...previousResult,
                  spaceById: {
                    ...previousResult.spaceById!,
                    __typename: "Space",
                    availableSitesBySpaceId: {
                      __typename: "SitesConnection",
                      nodes: siteNodes,
                      totalCount: currentLinkData!.totalCount,
                      pageInfo: {
                        ...currentLinkData!.pageInfo,
                      },
                    },
                  },
                };
              },
              variables,
            }),
        };
        return { ...tmpData, data };
      } else {
        return { ...(ownProps as SiteListProps), data: data! };
      }
    },
  }),
  withCreateFile({
    name: "createFile",
  }),
  withUpdateSiteById({
    name: "updateSiteById",
  }),
);
